import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useAppForm } from "@/components/form/form-context";
import { useMutation } from "@tanstack/react-query";
import { resetPasswordFn } from "@/server/new-password";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import logo from "@/assets/logo.png";
import pkg from "../../package.json";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { login } from "@/server/auth/login";
import { getNextRedirectRoute } from "@/lib/utils";

export const Route = createFileRoute("/password-expired")({
  component: PasswordExpiredForm,
  loader: async ({ context, location }) => {
    if (!context.user?.passwordExpiredFlow) {
      throw redirect({
        to: "/login",
        search: { redirect: location.href },
      });
    }
    return {
      username: context.user?.username,
    };
  },
});

function PasswordExpiredForm() {
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const { username } = Route.useLoaderData();
  const packageVersion = pkg.version + "-" + (pkg as any).config?.release;
  const currentYear = new Date().getFullYear();
  const router = useRouter();

  const handleConfirmPasswordChange = (value: string) => {
    if (passwordError) {
      setPasswordError("");
    }
    setConfirmPassword(value);
  };

  const passwordExpiredMutation = useMutation({
    mutationFn: resetPasswordFn,
  });

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: async (result) => {
      await router.invalidate();
      if (result.profile !== undefined && result.accessControl) {
        const user = {
          profile: result.profile,
          accessControl: result.accessControl,
        } as any;
        const nextRoute = getNextRedirectRoute(user);
        router.navigate({ to: nextRoute });
      } else {
        router.navigate({ to: "/gateways" });
      }
    },
  });

  const form = useAppForm({
    defaultValues: {
      password: "",
    },
    onSubmit: async ({ value }) => {
      if (value.password && value.password !== confirmPassword) {
        setPasswordError("Passwords do not match");
        return;
      } else {
        setPasswordError("");
      }
      let apiData;
      apiData = {
        password: value.password,
      };
      setDisableSubmit(true);
      const passwordExpiredPromise = passwordExpiredMutation.mutateAsync({ data: apiData });
      toast.promise(passwordExpiredPromise, {
        loading: "Changing password...",
        success: () => {
          //When the password has been changed, we need to login again to create a new session.
          const loginPromise = loginMutation.mutateAsync({
            data: {
              username: username || "",
              password: value.password,
            },
          });
          toast.promise(loginPromise, {
            loading: "Redirecting...",
            error: (error) => {
              setDisableSubmit(false);
              return error.message || "An error occurred while logging in";
            },
          });
          return "Password changed successfully";
        },
        error: (error) => {
          setDisableSubmit(false);
          return error.message || "An error occurred while changing password";
        },
      });
    },
  });
  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-muted p-6 md:p-10 w-full">
      <div className="w-full max-w-sm md:max-w-4xl">
        <div className="flex flex-col gap-6">
          <Card className="overflow-hidden p-0">
            <CardContent className="grid p-0 md:grid-cols-2">
              <div className="p-8 flex flex-col justify-center">
                <div className="flex justify-center md:hidden">
                  <img src={logo} alt="Logo" className="h-30 object-contain dark:invert" />
                </div>
                <CardHeader>
                  <CardTitle className="text-2xl">Password Expired</CardTitle>
                  <CardDescription>
                    Your password has expired. You cannot access any resources until you change your password.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form
                    method="POST"
                    className="p-6 md:p-8"
                    onSubmit={(e) => {
                      e.preventDefault();
                      form.handleSubmit();
                    }}
                  >
                    <div className="flex flex-col gap-6">
                      {passwordError && (
                        <Alert variant="destructive">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>{passwordError}</AlertDescription>
                        </Alert>
                      )}
                      <form.AppField
                        name="password"
                        validators={{
                          onChange: () => {
                            // Clear password confirmation error when password changes
                            setPasswordError("");
                            return undefined;
                          },
                        }}
                      >
                        {(field: any) => (
                          <field.FormTextField
                            label="New password *"
                            id="password"
                            type="password"
                            placeholder="Enter password"
                            autoComplete="new-password"
                          />
                        )}
                      </form.AppField>
                      <div className="flex flex-col gap-2 w-full">
                        <Label htmlFor="confirmPassword" className="text-sm font-medium">
                          Confirm Password *
                        </Label>
                        <div className="relative flex-1">
                          <Input
                            id="confirmPassword"
                            type={showPassword ? "text" : "password"}
                            placeholder="Repeat password"
                            autoComplete="new-password"
                            value={confirmPassword || ""}
                            onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                          />
                          {confirmPassword && confirmPassword !== "" && confirmPassword !== null && (
                            <button
                              type="button"
                              className="absolute inset-y-0 right-3 flex items-center text-gray-500"
                              onClick={() => setShowPassword((prev) => !prev)}
                              aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                              {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                            </button>
                          )}
                        </div>
                      </div>
                      <form.Subscribe
                        selector={(state) => [state.isSubmitting, state.isDirty, state.canSubmit]}
                        children={([isSubmitting, isDirty, canSubmit]) => (
                          <Button
                            type="submit"
                            className="w-full"
                            disabled={
                              isSubmitting ||
                              !isDirty ||
                              !canSubmit ||
                              passwordExpiredMutation.isPending ||
                              loginMutation.isPending ||
                              passwordError !== "" ||
                              confirmPassword === "" ||
                              confirmPassword === null ||
                              disableSubmit
                            }
                          >
                            {passwordExpiredMutation.isPending ? "Saving..." : "Save"}
                          </Button>
                        )}
                      />
                    </div>
                  </form>
                </CardContent>
              </div>

              <div className="relative hidden bg-muted md:block">
                <img src={logo} alt="Logo" className="absolute inset-0 h-full w-full object-cover dark:invert" />
              </div>
            </CardContent>
          </Card>

          <div className="text-center text-xs space-y-2 text-muted-foreground">
            <div>Client Version: {packageVersion}</div>
            <div>© nWise {currentYear}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
